import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Mail, Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const PurchaseNudgeCard = ({
  nudgePreview, sending, onSend,
  daysBefore = 10, onDaysBeforeChange,
}) => {
  const { t } = useLanguage();
  const disabled = sending || !nudgePreview || nudgePreview.outstanding === 0;
  // Ephemeral local string state so the field can be cleared and retyped
  // without snapping back to the last-committed number on every keystroke
  // (Session 54 LOW #2 — the controlled input's `value` had been driven
  // directly by the numeric prop, which re-rendered as e.g. "10" the
  // instant the user cleared it).
  const [inputValue, setInputValue] = useState(String(daysBefore));
  useEffect(() => {
    // Keep local state in sync when the parent commits a new value
    // (e.g. clamped from a stale API response).
    setInputValue(String(daysBefore));
  }, [daysBefore]);

  const handleDaysChange = (e) => {
    const raw = e.target.value;
    setInputValue(raw);
    if (raw === '') return;  // allow retyping — don't refetch on empty
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(365, Math.max(1, parsed));
    onDaysBeforeChange?.(clamped);
  };

  const handleBlur = () => {
    // On blur, if the field is empty or invalid, restore to the last
    // committed prop value so the display never lingers on an empty box.
    if (inputValue === '' || Number.isNaN(parseInt(inputValue, 10))) {
      setInputValue(String(daysBefore));
    }
  };

  return (
    <Card className="bg-card border-border mb-10" data-testid="purchase-nudge-card">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-base font-bold mb-1">{t('analytics.nudgeTitle')}</h3>
              <p className="text-sm text-muted-foreground mb-2">{t('analytics.nudgeDesc')}</p>

              {/* days_before input: change → live-refetch preview so the admin
                  can dial in the sweet spot for their retention setting. */}
              <div className="flex items-center gap-2 mb-2">
                <Label htmlFor="nudge-days-before" className="text-xs text-muted-foreground">
                  {t('analytics.nudgeDaysBeforeLabel')}
                </Label>
                <Input
                  id="nudge-days-before"
                  type="number"
                  min={1}
                  max={365}
                  value={inputValue}
                  onChange={handleDaysChange}
                  onBlur={handleBlur}
                  className="w-20 h-8 text-sm"
                  data-testid="nudge-days-before-input"
                />
              </div>

              {nudgePreview ? (
                <>
                  <div className="text-sm font-mono" data-testid="purchase-nudge-counts">
                    <span className="text-muted-foreground">{t('analytics.nudgeOutstanding')}: </span>
                    <span className="font-bold text-emerald-500" data-testid="purchase-nudge-outstanding">
                      {nudgePreview.outstanding}
                    </span>
                    <span className="text-muted-foreground"> · {t('analytics.nudgeAlreadyReminded')}: </span>
                    <span className="font-bold" data-testid="purchase-nudge-already">
                      {nudgePreview.already_nudged}
                    </span>
                    <span className="text-muted-foreground"> · {t('analytics.nudgeEligibleTotal')}: </span>
                    <span className="font-bold" data-testid="purchase-nudge-eligible">
                      {nudgePreview.eligible_total}
                    </span>
                  </div>
                  {typeof nudgePreview.photos_in_window === 'number' && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid="purchase-nudge-diagnostics">
                      {t('analytics.nudgeDiagnostics')
                        .replace('{photos}', String(nudgePreview.photos_in_window))
                        .replace('{min}', String(nudgePreview.window_age_days?.min ?? '?'))
                        .replace('{max}', String(nudgePreview.window_age_days?.max ?? '?'))
                        .replace('{retention}', String(nudgePreview.retention_days ?? '?'))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-muted-foreground">...</div>
              )}
            </div>
          </div>
          <Button
            onClick={onSend}
            disabled={disabled}
            className="bg-emerald-600 hover:bg-emerald-600/90 text-white"
            data-testid="send-purchase-nudges-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('analytics.nudgeSending')}</>
            ) : (
              <><Mail className="w-4 h-4 mr-2" />{t('analytics.nudgeSendButton')}</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
